import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, Save } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { inputCls, L, SectionCard, RteField, Repeater, BannerField } from "./MarathonLandingParts";

export default function MarathonLanding() {
  const [d, setD] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/marathon/landing").then(({ data }) => setD(data)).catch((e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load"));
  }, []);

  const sec = (k, patch) => setD((x) => ({ ...x, [k]: { ...x[k], ...patch } }));
  const common = (k) => ({ sec: d[k], onToggle: (v) => sec(k, { visible: v }), onHeading: (v) => sec(k, { heading: v }) });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/marathon/landing", d);
      setD(data); toast.success("Landing page saved");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!d) return <div className="text-sm text-[#6b655b]" data-testid="ml-loading">Loading…</div>;
  return (
    <div className="space-y-5" data-testid="admin-marathon-landing">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">Marathon</div>
          <h1 className="font-serif text-2xl text-[#1c1a17]">Landing Page</h1>
          <div className="text-sm text-[#6b655b] mt-0.5">Public page: <a href="/marathon" target="_blank" rel="noreferrer" className="text-[#234333] underline inline-flex items-center gap-1" data-testid="ml-public-link">/marathon <ExternalLink size={12} /></a> · Run categories come from Run Types & Fees.</div>
        </div>
      </div>

      <SectionCard id="hero" title="Hero" sec={{ visible: true }} alwaysOn>
        <BannerField url={d.banner_url} onChanged={(x) => setD((p) => ({ ...p, banner_url: x.banner_url, banner_public_id: x.banner_public_id }))} />
        <div className="grid md:grid-cols-2 gap-4">
          <L label="Title" hint="Last word is highlighted. Blank = Marathon name from Masters."><input className={inputCls} value={d.hero.title} maxLength={120} onChange={(e) => sec("hero", { title: e.target.value })} data-testid="ml-hero-title" /></L>
          <L label="Event date text" hint="e.g. Sunday, 20 December 2026 · 5:30 AM"><input className={inputCls} value={d.hero.event_date_text} maxLength={120} onChange={(e) => sec("hero", { event_date_text: e.target.value })} data-testid="ml-hero-date" /></L>
          <div className="md:col-span-2"><L label="Tagline"><textarea rows={2} className={inputCls} value={d.hero.tagline} maxLength={400} onChange={(e) => sec("hero", { tagline: e.target.value })} data-testid="ml-hero-tagline" /></L></div>
          <L label="Register button label"><input className={inputCls} value={d.hero.cta_label} maxLength={40} onChange={(e) => sec("hero", { cta_label: e.target.value })} data-testid="ml-hero-cta" /></L>
        </div>
      </SectionCard>

      <SectionCard id="about" title="About the Run" {...common("about")}>
        <RteField label="Content" value={d.about.html} onChange={(v) => sec("about", { html: v })} testid="ml-about" placeholder="Why we run, the cause, history…" />
      </SectionCard>

      <SectionCard id="categories" title="Run Categories" {...common("categories")}>
        <L label="Intro text" hint="Distance, fee, age limit and slots left are shown automatically from Run Types & Fees."><textarea rows={2} className={inputCls} value={d.categories.intro} maxLength={600} onChange={(e) => sec("categories", { intro: e.target.value })} data-testid="ml-categories-intro" /></L>
      </SectionCard>

      <SectionCard id="schedule" title="Event-Day Schedule" {...common("schedule")}>
        <Repeater testid="ml-schedule" items={d.schedule.items} onChange={(items) => sec("schedule", { items })} blank={{ time: "", title: "", detail: "" }} addLabel="Add schedule item"
          render={(it, set) => (
            <div className="grid md:grid-cols-[120px_1fr] gap-2">
              <input className={inputCls} placeholder="05:00 AM" value={it.time} onChange={(e) => set({ time: e.target.value })} />
              <input className={inputCls} placeholder="Bib collection & warm-up" value={it.title} onChange={(e) => set({ title: e.target.value })} />
              <input className={`${inputCls} md:col-span-2`} placeholder="Details (optional)" value={it.detail} onChange={(e) => set({ detail: e.target.value })} />
            </div>
          )} />
      </SectionCard>

      <SectionCard id="route" title="Route & Venue" {...common("route")}>
        <RteField label="Content" value={d.route.html} onChange={(v) => sec("route", { html: v })} testid="ml-route" placeholder="Start / finish point, route description, parking…" />
        <L label="Google Maps link" hint="Paste the Share link from Google Maps (https://…)"><input className={inputCls} value={d.route.map_url} maxLength={600} onChange={(e) => sec("route", { map_url: e.target.value })} data-testid="ml-route-map" /></L>
      </SectionCard>

      <SectionCard id="prizes" title="Prizes & Goodies" {...common("prizes")}>
        <RteField label="Content" value={d.prizes.html} onChange={(v) => sec("prizes", { html: v })} testid="ml-prizes" placeholder="Finisher medal, T-shirt, prizes per category…" />
      </SectionCard>

      <SectionCard id="faqs" title="FAQs" {...common("faqs")}>
        <Repeater testid="ml-faqs" items={d.faqs.items} onChange={(items) => sec("faqs", { items })} blank={{ q: "", a: "" }} addLabel="Add FAQ"
          render={(it, set) => (
            <div className="space-y-2">
              <input className={inputCls} placeholder="Question" value={it.q} onChange={(e) => set({ q: e.target.value })} />
              <textarea rows={2} className={inputCls} placeholder="Answer" value={it.a} onChange={(e) => set({ a: e.target.value })} />
            </div>
          )} />
      </SectionCard>

      <SectionCard id="contact" title="Contact" {...common("contact")}>
        <div className="grid md:grid-cols-3 gap-4">
          <L label="Phone"><input className={inputCls} value={d.contact.phone} onChange={(e) => sec("contact", { phone: e.target.value })} data-testid="ml-contact-phone" /></L>
          <L label="WhatsApp"><input className={inputCls} value={d.contact.whatsapp} onChange={(e) => sec("contact", { whatsapp: e.target.value })} data-testid="ml-contact-whatsapp" /></L>
          <L label="Email"><input className={inputCls} value={d.contact.email} onChange={(e) => sec("contact", { email: e.target.value })} data-testid="ml-contact-email" /></L>
          <div className="md:col-span-3"><L label="Address"><textarea rows={2} className={inputCls} value={d.contact.address} onChange={(e) => sec("contact", { address: e.target.value })} data-testid="ml-contact-address" /></L></div>
        </div>
      </SectionCard>

      <div className="sticky bottom-0 -mx-2 bg-white/95 backdrop-blur border border-[#e5e0d8] rounded-sm px-6 py-3 flex items-center justify-end gap-3 z-30">
        {d.updated_at && <span className="text-[11px] text-[#8a8478]">Last saved {new Date(d.updated_at).toLocaleString()}</span>}
        <button type="button" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1a3326] text-white px-5 py-2.5 text-sm font-semibold rounded-sm disabled:opacity-60" data-testid="ml-save"><Save size={15} />{saving ? "Saving…" : "Save Landing Page"}</button>
      </div>
    </div>
  );
}
